import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import {
  generateFollowUpUploadPresignedUrls,
  getFollowUpDiscussionDetails,
  submitFollowUpApplicationDetails,
} from "../../api/followUpDiscussion";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.pdf,.mp4";
const ACCEPTED_HELP = "JPEG, PNG, PDF, and MP4 formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_DOCUMENTS = 20;
const FILE_INPUT_ID = "follow-up-application-details-file-input";

type SavedDocument = {
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
};

type PendingDocument = {
  id: string;
  file: File;
};

type FollowUpApplicationDetailsFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export const FOLLOW_UP_APPLICATION_DETAILS_FORM_ID =
  "follow-up-application-details-form";

export default function FollowUpApplicationDetailsFromClientView({
  leadId,
  onSuccess,
}: FollowUpApplicationDetailsFromClientViewProps) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const pendingIdRef = useRef(0);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setSavedDocuments([]);
      setPendingDocuments([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpDiscussionDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        setSavedDocuments(
          data.applicationDetails.documents.map((d) => ({
            fileKey: d.fileKey,
            fileName: d.fileName,
            fileUrl: d.fileUrl,
          }))
        );
        setPendingDocuments([]);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load application details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const totalCount = savedDocuments.length + pendingDocuments.length;

  const addFiles = (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    const remainingSlots = MAX_DOCUMENTS - totalCount;
    if (remainingSlots <= 0) {
      toast.error(`You can attach up to ${MAX_DOCUMENTS} documents`);
      return;
    }

    const accepted: PendingDocument[] = [];
    for (const file of list.slice(0, remainingSlots)) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      pendingIdRef.current += 1;
      accepted.push({ id: `pending-${pendingIdRef.current}`, file });
    }
    if (list.length > remainingSlots) {
      toast.error(`You can attach up to ${MAX_DOCUMENTS} documents`);
    }
    if (accepted.length > 0) {
      setPendingDocuments((prev) => [...prev, ...accepted]);
    }
  };

  const removePending = (id: string) => {
    setPendingDocuments((prev) => prev.filter((d) => d.id !== id));
  };

  const removeSaved = (fileKey: string) => {
    setSavedDocuments((prev) => prev.filter((d) => d.fileKey !== fileKey));
  };

  const openPendingPreview = (doc: PendingDocument) => {
    const url = URL.createObjectURL(doc.file);
    setPreview({
      url,
      name: doc.file.name,
      kind: detectPreviewKind(doc.file.name, doc.file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: SavedDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({
      url: doc.fileUrl,
      name: doc.fileName,
      kind: detectPreviewKind(doc.fileName),
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    if (totalCount === 0) {
      toast.error("Please upload at least one document");
      return;
    }

    setSaving(true);
    try {
      let newlyUploaded: SavedDocument[] = [];
      if (pendingDocuments.length > 0) {
        const presigned = await generateFollowUpUploadPresignedUrls(
          leadId,
          pendingDocuments.map((p) => ({
            filename: p.file.name,
            mimeType: p.file.type || "application/octet-stream",
          }))
        );

        if (presigned.length !== pendingDocuments.length) {
          throw new Error("Failed to generate upload URLs for all files");
        }

        for (let i = 0; i < pendingDocuments.length; i++) {
          const target = presigned[i];
          const item = pendingDocuments[i];
          const putRes = await fetch(target.url, {
            method: "PUT",
            body: item.file,
            headers: {
              "Content-Type": item.file.type || "application/octet-stream",
            },
          });
          if (!putRes.ok) {
            throw new Error(`Failed to upload ${item.file.name}`);
          }
          newlyUploaded.push({
            fileKey: target.key,
            fileName: item.file.name,
            fileUrl: null,
          });
        }
      }

      const allDocs = [...savedDocuments, ...newlyUploaded];
      await submitFollowUpApplicationDetails(leadId, {
        documents: allDocs.map((d) => ({
          fileKey: d.fileKey,
          fileName: d.fileName,
        })),
      });

      setSavedDocuments(allDocs);
      setPendingDocuments([]);
      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save application details"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex flex-col gap-2 self-stretch">
          <p
            className="font-semibold text-[#101111]"
            style={{
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0.01em",
            }}
          >
            Upload Documents
          </p>
          <p
            className="font-normal text-[#7D7D7D]"
            style={{
              fontSize: "12px",
              lineHeight: "24px",
              letterSpacing: "0.04em",
            }}
          >
            Choose a file and upload securely to proceed.
          </p>
        </div>

        <div
          className={`self-stretch flex flex-row justify-between items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
            dragging
              ? "border-[#222222] bg-[#F5F5F5]"
              : "border-[#D8DAEB]"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (saving) return;
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              addFiles(e.dataTransfer.files);
            }
          }}
          onClick={() => {
            if (saving) return;
            document.getElementById(FILE_INPUT_ID)?.click();
          }}
        >
          <div className="flex flex-row items-center gap-5 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={60}
              height={60}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-3 min-w-0">
              <p
                className="font-medium text-[#101111]"
                style={{
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0.01em",
                }}
              >
                Drag and drop your files
              </p>
              <p
                className="font-normal text-[#7D7D7D]"
                style={{ fontSize: "14px", lineHeight: "21px" }}
              >
                {ACCEPTED_HELP}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById(FILE_INPUT_ID)?.click();
            }}
            className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Select File
          </button>
        </div>

        <input
          id={FILE_INPUT_ID}
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              addFiles(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {totalCount > 0 && (
          <div className="flex flex-col gap-2 self-stretch">
            {savedDocuments.map((doc) => (
              <div
                key={`saved-${doc.fileKey}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeSaved(doc.fileKey)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {pendingDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {doc.file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${doc.file.name}`}
                    onClick={() => removePending(doc.id)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}
